import React, { Component, PropTypes } from 'react';
import classnames from 'classnames';
import idx from 'idx';
import without from 'lodash/without';
import union from 'lodash/union';
import { LoadingIndicator } from 'common/components/preloader';

/* mapping between Braintree credit card types and Font Awesome icons whose names
   don't match exactly */
const CC_ICONS = {
  'discover' : 'discover',
  'visa' : 'visa',
  'master-card': 'mastercard',
  'american-express': 'amex'
}

/**
 * Custom Braintree component used to add a new payment method, which
 * can be either a credit card or a PayPal account.
 *
 * NOTE: for control purposes, the submission of the form must be performed by the parent
 * component using the submit() method (e.g. `this.refs.form_name.submit()`)
 *
 * @author Nathan Ozelim <nathan@startupfoundry.com>
 */
export default class BraintreeForm extends Component {
  constructor(props) {
    super(props);

    this.state = {
      method: null, cardType: '', focusedField: null, errorFields: [], filledFields: []
    };
    this.setup = this.setup.bind(this);
    this.handleError = this.handleError.bind(this);
    this.handleFieldEvent = this.handleFieldEvent.bind(this);
    this.handleMethodReceived = this.handleMethodReceived.bind(this);
    this.handlePaypalLogin = this.handlePaypalLogin.bind(this);
    this.handlePaypalLogout = this.handlePaypalLogout.bind(this);
    this.handleCreditCardClick = this.handleCreditCardClick.bind(this);
    this.handlePaypalClick = this.handlePaypalClick.bind(this);
    this.submit = this.submit.bind(this);
  }

  handleCreditCardClick() {
    if (!this.state.method) {
      this.setState({ method: "credit_card" });
    }
  }

  handlePaypalClick() {
    if (this.state.method !== "paypal") {
      const paypalBtnEl = document.getElementById("braintree-paypal-button");
      if (paypalBtnEl) {
        paypalBtnEl.click();
      }
    }
  }

  handlePaypalLogin() {
    this.setState({ method: "paypal" });
  }

  handlePaypalLogout() {
    this.setState({ method: null });
  }

  handleError(err) {
    this.props.onError(err.message)
  }

  handleMethodReceived(method) {
    const { saveMethod, onSuccess, onError, fetchMethodIfNeeded } = this.props
    saveMethod(method.nonce).then(() => {
      onSuccess()
      /* fetch updated payment method */
      fetchMethodIfNeeded(true)
    }).catch(({data}) => {
      onError((data && data.message)? data.message : 'Failed to save payment method')
    })
  }

  handleFieldEvent(e) {
    const { errorFields, filledFields } = this.state;
    const fieldId = idx(e, _ => _.target.container.id);

    if (e.type == 'focus') {
      this.setState({ error: null, focusedField: fieldId });
    } else if (e.type == 'blur') {
      this.setState({ error: null, focusedField: null });
    } else if (e.type == 'fieldStateChange') {
      this.setState({
        cardType: e.card? e.card.type : null,
        errorFields: e.isPotentiallyValid? without(errorFields, fieldId) : union(errorFields, [fieldId]),
        filledFields: e.isEmpty? without(filledFields, fieldId) : union(filledFields, [fieldId])
      });
    }
  }

  setup(token) {
    if (typeof braintree === "undefined") {
      console.warn("Braintree not loaded upon form setup - ignoring");
      return;
    }

    braintree.setup(token, "custom", {
      id: "braintree_form",
      paypal: {
        container: "paypal_container",
        onSuccess: this.handlePaypalLogin,
        onCancelled: this.handlePaypalLogout
      },
      hostedFields: {
        /* the "internal" styling of the fields (meaning the text) is configured via JavaScript
           https://developers.braintreepayments.com/guides/hosted-fields/styling/javascript/v2 */
        styles: {
          'input': {
            'font-size': '23px',
            'line-height': '1',
          },
          '::-webkit-input-placeholder': {
            'opacity': '0.3',
          },
          ':-moz-placeholder': {
            'opacity': '0.3',
          },
          '::-moz-placeholder': {
            'opacity': '0.3',
          },
          ':-ms-input-placeholder': {
            'opacity': '0.3',
          },
          '::-ms-input-placeholder': {
            'opacity': '0.3',
          },
          '.invalid': {
            'color': '#A70045'
          }
        },

        onFieldEvent: this.handleFieldEvent,

        number: {
          selector: "#card_number",
          placeholder: '1111111111111111'
        },
        cvv: {
          selector: "#cvv",
          placeholder: '111'
        },
        expirationDate: {
          selector: "#expiration_date",
          placeholder: '03/25'
        }
      },

      onError: this.handleError,
      onPaymentMethodReceived: this.handleMethodReceived,
    })
  }

  componentDidMount() {
    this.props.fetchTokenIfNeeded().then(this.setup)
  }

  submit() {
    /* allow submission by parent components by emulating click on (hidden) submit button */
    this.setState({error: null})
    this.refs.submit_btn.click()
  }

  renderCreditCard(hidden) {
    const { cardType, focusedField, filledFields, errorFields } = this.state;

    return (
      <div className={classnames("credit-card", { hide: hidden })}>
        <div className="row">
          <div className="columns small-16 large-12">
            <div className={classnames("form-group", {
              focused: focusedField === "card_number",
              filled: filledFields.includes("card_number"),
              error: errorFields.includes("card_number")
            })}>
              <div className="card-logo">
                { cardType && <i className={"cc-type fa fa-cc-" + (CC_ICONS[cardType] || cardType)}/> }
              </div>
              <label htmlFor="card_number">Card number (no dashes or spaces)</label>
              <div id="card_number"></div>
              { errorFields.includes("card_number") &&
              <span className="error-msg">Please provide a valid card number</span> }
            </div>
          </div>
        </div>

        <div className="row">
          <div className="columns small-8 large-6">
            <div className={classnames("form-group", {
              focused: focusedField === "expiration_date",
              filled: filledFields.includes("expiration_date"),
              error: errorFields.includes("expiration_date")
            })}>
              <label htmlFor="expiration_date">Expiration date</label>
              <div id="expiration_date"></div>
              { errorFields.includes("expiration_date") &&
              <span className="error-msg">Please provide a valid expiration date</span> }
            </div>
          </div>

          <div className="columns small-8 large-6">
            <div className={classnames("form-group", {
              focused: focusedField === "cvv",
              filled: filledFields.includes("cvv"),
              error: errorFields.includes("cvv")
            })}>
              <label htmlFor="cvv">Security code (CVV)</label>
              <div id="cvv"></div>
              { errorFields.includes("cvv") &&
              <span className="error-msg">Please provide a valid security code</span> }
            </div>
          </div>
        </div>
      </div>
    );
  }

  renderPaypal(hidden) {
    return (
      <div className={classnames("paypal", { hide: hidden })}>
        <div id="paypal_container"></div>
      </div>
    );
  }

  render() {
    const { fetchingToken } = this.props;
    const { method } = this.state;

    if (fetchingToken) return <LoadingIndicator fullScreen={false}/>

    return (
      <form id="braintree_form">
        <div className="method-selector">
          <div className={classnames("method", {
            active: method === "credit_card",
            disabled: method === "paypal"
          })} onClick={this.handleCreditCardClick}>
            <i className="fa fa-credit-card" /> Credit or debit card
          </div>
          <div className={classnames("method", { active: method === "paypal" })}
              onClick={this.handlePaypalClick}>
            <i className="fa fa-cc-paypal" /> PayPal
          </div>
        </div>

        { this.renderCreditCard(method !== "credit_card") }

        { this.renderPaypal(method !== "paypal") }

        {/* hidden button whose sole purpose is to allow form submission from parent components */}
        <button className="hide" ref="submit_btn"></button>
      </form>
    );
  }
}

/**
 * PropTypes.
 *
 * @property {func} saveMethod - save new payment method
 * @property {func} onSuccess - callback invoked when method is successfully added
 * @property {func} onError - callback invoked when there's an error adding the payment method
 * @property {func} fetchTokenIfNeeded - fetch Braintree client token if needed
 * @property {bool} fetchingToken - whether Braintree client token is being fetched
 */
BraintreeForm.propTypes = {
  saveMethod: PropTypes.func,
  onSuccess: PropTypes.func,
  onError: PropTypes.func,
  fetchTokenIfNeeded: PropTypes.func,
  fetchingToken: PropTypes.bool
};



// WEBPACK FOOTER //
// ./common/components/braintree_form.jsx